import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    ClearFields,
    SingleCheckOptionItem,
    SingleHorizontalOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { Observation } from '@/components/features/observation';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useDistributionBoards } from '@/hooks/use-distribution-board';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { useOnline } from '@/hooks/use-online';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/information/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions(
                { electricalProjectVersionId: params.projectVersionId },
            ),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.distributionBoard.options.distributionBoardValuesOptions(),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.distributionBoard.options.ingressProtectionRatingsOptions(),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.observationsListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.observations.standardTextsGetOptions(
                {
                    electricalProjectVersionId: params.projectVersionId,
                    objectKind: 'DistributionBoard',
                },
            ),
        );
        context.queryClient.ensureQueryData(
            elektraAppQueryOptions.project.inspection.observations.classificationGetOptions(
                {
                    electricalProjectVersionId: params.projectVersionId,
                },
            ),
        );
    },
});

type VerdelerInformatieFormValues = {
    identifier: string;
    kind:
        | components['schemas']['ElectricalProjectDistributionBoardKind']
        | null;
    floorNumber: string;
    spaceNumber: string;
    parentElectricalProjectDistributionBoardId: string | null;
    isPVInstallation: boolean | null;
};

type GeneralInformationFormValues = {
    electricalIngressProtectionRatingId: string;
    earthingSystemKind: components['schemas']['ElectricalEarthingSystemKind'] | null;
    powerPhaseKind: components['schemas']['ElectricalPowerPhaseKind'] | null;
    applicanceProtectionClass: components['schemas']['ElectricalApplicanceProtectionClass'] | null;
    mainSwitchNominalCurrent: number | null;
    mainSwitchNominalCurrentCustom: number | null;
    upstreamProtectiveDeviceKind: string;
    upstreamProtectiveDeviceCharacteristic: string;
    upstreamProtectiveDeviceCharacteristicCustom: string;
    powerCircuitBreakerType: string;
    powerCircuitBreakerSetting: string;
    powerCircuitBreakerCurrent: number | null;
};

function Page() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();

    return (
        <div className="flex flex-col gap-4">
            <Container>
                <Container.Header>
                    <Container.Title>
                        {t('distribution-box.information.navigation.verdeler-informatie')}
                    </Container.Title>
                </Container.Header>
                <Container.Content>
                    <InformationForm />
                </Container.Content>
            </Container>

            <Photos />

            <Observation.Root
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardInformation"
                objectId={params.distributionboardId}
            >
                <Container>
                    <Container.Header>
                        <Container.Title>
                            {t('distribution-box.information.navigation.general-information')}
                        </Container.Title>
                    </Container.Header>
                    <Container.Content>
                        <GeneralInformationForm />
                    </Container.Content>
                    <Section.Footer>
                        <FooterNavigation />
                    </Section.Footer>
                </Container>
            </Observation.Root>
        </div>
    );
}

function InformationForm() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();

    const { distributionBoard, distributionBoards } = useDistributionBoards();

    const { inspectionKindsArray, hasInspectionKind } = useElectricalProject();

    const isCombinationInspection = useMemo(() => inspectionKindsArray.length > 1, [inspectionKindsArray]);

    const { Field, handleSubmit, Subscribe } = useForm<
        VerdelerInformatieFormValues,
        ZodValidator
    >({
        defaultValues: {
            identifier: distributionBoard?.Identifier || '',
            kind: distributionBoard?.Kind || null,
            floorNumber: distributionBoard?.FloorNumber || '',
            spaceNumber: distributionBoard?.SpaceNumber || '',
            parentElectricalProjectDistributionBoardId:
                distributionBoard?.ParentElectricalProjectDistributionBoardId
                || null,
            isPVInstallation: booleanDefaultValue(distributionBoard?.IsPVInstallation, !isCombinationInspection ? true : null),
        },
        validatorAdapter: zodValidator(),
    });

    const renderOptions = useMemo(
        () =>
            distributionBoards
                .filter(
                    db =>
                        db.ElectricalProjectDistributionBoardId
                        !== params.distributionboardId,
                )
                .map(db => ({
                    label: db.Identifier || 'Naamloze verdeelinrichting',
                    value: db.ElectricalProjectDistributionBoardId!,
                })),
        [distributionBoards, params.distributionboardId],
    );

    const patcher = usePatcher(
        '/api/v2/electrical/projects/distributionboards/patch',
        {
            onMutate: async (variables) => {
                const body = toPascalCase(variables!.body!);

                const distributionBoardQueryOptions
                    = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions(
                        {
                            electricalProjectDistributionBoardId:
                                params.distributionboardId,
                        },
                    );
                const distributionBoardQueryListOptions
                    = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions(
                        {
                            electricalProjectVersionId: params.projectVersionId,
                        },
                    );

                await queryClient.cancelQueries(distributionBoardQueryOptions);
                await queryClient.cancelQueries(
                    distributionBoardQueryListOptions,
                );

                const previousDistributionBoardData = queryClient.getQueryData(
                    distributionBoardQueryOptions.queryKey,
                ) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];
                const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult']
                    = {
                        ...previousDistributionBoardData,
                        DistributionBoard: {
                            ...previousDistributionBoardData.DistributionBoard,
                            ...body,
                        },
                    };

                const previousDistributionBoardListData
                    = queryClient.getQueryData(
                        distributionBoardQueryListOptions.queryKey,
                    ) as components['schemas']['ListElectricalProjectDistributionBoardQueryResult'];
                const newDistributionBoardListData: components['schemas']['ListElectricalProjectDistributionBoardQueryResult']
                    = {
                        ...previousDistributionBoardListData,
                        DistributionBoards:
                        previousDistributionBoardListData.DistributionBoards.map(
                            (db) => {
                                if (
                                    db.ElectricalProjectDistributionBoardId
                                    === params.distributionboardId
                                ) {
                                    return {
                                        ...db,
                                        ...newDistributionBoardData.DistributionBoard,
                                    };
                                }
                                return db;
                            },
                        ),
                    };

                logger.debug({
                    message: 'onMutate(distributionBoardQueryOptions)',
                    path: '/api/v2/electrical/projects/distributionboards/patch',
                    distributionBoard: {
                        previousData: previousDistributionBoardData,
                        newData: newDistributionBoardData,
                        queryKey: distributionBoardQueryOptions.queryKey,
                        body,
                    },
                    distributionBoardList: {
                        previousData: previousDistributionBoardListData,
                        newData: newDistributionBoardListData,
                        queryKey: distributionBoardQueryListOptions.queryKey,
                    },
                });

                queryClient.setQueryData(
                    distributionBoardQueryOptions.queryKey,
                    newDistributionBoardData,
                );
                queryClient.setQueryData(
                    distributionBoardQueryListOptions.queryKey,
                    newDistributionBoardListData,
                );

                return {
                    previouseData: previousDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                };
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions(
                        {
                            electricalProjectDistributionBoardId:
                                params.distributionboardId,
                        },
                    ),
                );
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions(
                        {
                            electricalProjectVersionId: params.projectVersionId,
                        },
                    ),
                );
            },
            scope: {
                id: params.projectVersionId,
            },
        },
        {
            body: {
                ElectricalProjectDistributionBoardId:
                    params.distributionboardId,
            },
        },
    );

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field
                name="identifier"
                validators={{
                    onChange:
                        validators
                            .patchElectricalProjectDistributionBoardCommand
                            .identifier,
                    onChangeAsync: async (value) => {
                        const status
                            = await patcher.patchField('identifier')(value);

                        queryClient.invalidateQueries(
                            elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions(
                                {
                                    electricalProjectDistributionBoardId:
                                        params.distributionboardId,
                                },
                            ),
                        );

                        return status;
                    },
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.name.label')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t('distribution-box.information.form.name.placeholder')}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="kind"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.kind,
                    onChangeAsync: patcher.patchField('kind'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('installation.final-circuit.modal.form.type.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row"
                            options={[
                                { label: t('distribution-box.information.form.kind.MainDistributionBoard'), value: 'MainDistributionBoard' },
                                { label: t('distribution-box.information.form.kind.SubDistributionBoard'), value: 'SubDistributionBoard' },
                                { label: t('distribution-box.information.form.kind.ControlBox'), value: 'ControlBox' },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                    </Label>
                )}
            </Field>
            <Subscribe selector={state => [state.values.kind]}>
                {([kind]) =>
                    kind !== 'MainDistributionBoard' && (
                        <Field
                            name="parentElectricalProjectDistributionBoardId"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.parentElectricalProjectDistributionBoardId,
                                onChangeAsync: patcher.patchField('parentElectricalProjectDistributionBoardId'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('distribution-box.information.form.parentDistributionBoard.label')} className="relative">
                                    <ClearFields onClear={() => handleChange(null)} />
                                    <SingleOptionField
                                        handleChange={handleChange}
                                        name={name}
                                        state={state}
                                        render={SingleCheckOptionItem}
                                        options={renderOptions}
                                        dropdown
                                        closeOnClick
                                        placeholder={t('distribution-box.information.form.parentDistributionBoard.placeholder')}
                                        emptyOptionsMessage={t('distribution-box.information.form.parentDistributionBoard.emptyMessage')}
                                        search
                                    />
                                </Label>
                            )}
                        </Field>
                    )}
            </Subscribe>
            <Field
                name="floorNumber"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.floorNumber,
                    onChangeAsync: patcher.patchField('floorNumber'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.floor.label')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t('distribution-box.information.form.floor.placeholder')}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="spaceNumber"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.spaceNumber,
                    onChangeAsync: patcher.patchField('spaceNumber'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.room.label')}>
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t('distribution-box.information.form.room.placeholder')}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            {hasInspectionKind('Scope12') && (
                <Field
                    name="isPVInstallation"
                    validators={{
                        onChangeAsync: patcher.patchField('isPVInstallation'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state }) => (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={state.value ?? false}
                                onCheckedChange={checked => handleChange(checked === true)}
                            />
                            {t('distribution-box.information.form.isPVInstallation.label')}
                        </label>
                    )}
                </Field>
            )}
        </form>
    );
}

function Photos() {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const params = Route.useParams();

    const { distributionBoard, optimisticUpdate } = useDistributionBoards();

    const patcher = usePatcher(
        '/api/v2/electrical/projects/distributionboards/patch',
        {
            onMutate: async (variables) => {
                const body = toPascalCase(variables!.body!);

                const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });

                await queryClient.cancelQueries(distributionBoardQueryOptions);

                optimisticUpdate({
                    ...distributionBoard,
                    ...body,
                });

                return {
                    queryKey: distributionBoardQueryOptions.queryKey,
                };
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions(
                        {
                            electricalProjectDistributionBoardId:
                                params.distributionboardId,
                        },
                    ),
                );
            },
            scope: {
                id: params.projectVersionId,
            },
        },
        {
            body: {
                ElectricalProjectDistributionBoardId:
                    params.distributionboardId,
            },
        },
    );

    return (
        <>
            <Observation.Root
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardImages"
                objectId={params.distributionboardId}
            >
                <Container>
                    <Container.Header>
                        <Container.Title>
                            {t('distribution-box.information.navigation.fotos-verdeler')}
                        </Container.Title>
                    </Container.Header>
                    <Container.Content>
                        <Label label={t('distribution-box.information.form.enclosure-closed.label')} htmlFor="file-input-EnclosureClosedImage">
                            <FileField type="EnclosureClosedImage" />
                        </Label>
                        <Label label={t('distribution-box.information.form.enclosure-open.label')} htmlFor="file-input-EnclosureOpenImage">
                            <FileField type="EnclosureOpenImage" />
                        </Label>
                        <Label label={t('distribution-box.information.form.extra.label')} htmlFor="file-input-ExtraImage">
                            <FileField type="ExtraImage" />
                        </Label>
                        <Observation.Table />
                        <Observation.CreateButton className="mx-auto" />
                    </Container.Content>
                </Container>
            </Observation.Root>
            <Observation.Root
                electricalProjectVersionId={params.projectVersionId}
                objectKind="DistributionBoardThermalImages"
                objectId={params.distributionboardId}
            >
                <Container>
                    <Container.Header className="flex-row justify-between items-center">
                        <Container.Title>
                            {t('distribution-box.information.navigation.fotos-thermografie')}
                        </Container.Title>
                        <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                            <Checkbox
                                checked={distributionBoard?.ReportThermalImages !== false}
                                onCheckedChange={(checked) => {
                                    patcher.patchField('ReportThermalImages')({ value: checked });
                                }}
                            />
                            {c('report.include-in-report')}
                        </label>
                    </Container.Header>
                    <Container.Content>
                        <Label label={t('distribution-box.information.form.thermal.label')} htmlFor="file-input-thermalImage">
                            <FileField type="ThermalImage" />
                        </Label>
                        <Observation.Table />
                        <Observation.CreateButton className="mx-auto" />
                    </Container.Content>
                </Container>
            </Observation.Root>
        </>
    );
}

function FileField({ type }: { type: components['schemas']['ElectricalProjectDistributionBoardFileKind'] }) {
    const params = Route.useParams();
    const isOnline = useOnline();

    const { distributionBoard, optimisticUpdate } = useDistributionBoards();

    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            onMutate: async (variables) => {
                const electricalProjectDistributionBoardFileId = distributionBoard?.Files?.find(file => file.ElectricalProjectFileId === variables)?.ElectricalProjectDistributionBoardFileId;

                if (!electricalProjectDistributionBoardFileId) {
                    logger.warn({
                        message: 'onMutate(fileDelete)',
                        description: 'ElectricalProjectDistributionBoardFileId not found',
                        variables,
                    });

                    return;
                }

                optimisticUpdate({
                    ...distributionBoard,
                    Files: distributionBoard?.Files?.filter(file => file.ElectricalProjectDistributionBoardFileId !== electricalProjectDistributionBoardFileId),
                });
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    const linkMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/distributionboards/files/create',
        {
            onSettled: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId: params.projectVersionId }));
                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );
    const unlinkMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/distributionboards/files/delete',
        {
            onMutate: async (variables) => {
                const { params } = variables;

                const electricalProjectFileId = distributionBoard?.Files?.find(file => file.ElectricalProjectDistributionBoardFileId === params.query.ElectricalProjectDistributionBoardFileId)?.ElectricalProjectFileId;

                if (!electricalProjectFileId) {
                    logger.warn({
                        message: 'onMutate(unlinkMutation)',
                        description: 'ElectricalProjectFileId not found',
                        variables,
                    });

                    return;
                }

                fileDelete.delete(electricalProjectFileId);
            },
            onSettled: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId: params.projectVersionId }));
                queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/distributionboard',
        mutationOptions: {
            onMutate: async (file) => {
                const electricalProjectDistributionBoardFileId = crypto.randomUUID();

                linkMutation.mutate({
                    body: {
                        ElectricalProjectDistributionBoardId: params.distributionboardId,
                        ElectricalProjectDistributionBoardFileId: electricalProjectDistributionBoardFileId,
                        ElectricalProjectFileId: file.ElectricalProjectFileId,
                        Kind: type,
                    },
                });

                optimisticUpdate({
                    ...distributionBoard,
                    Files: [
                        ...(distributionBoard?.Files || []),
                        {
                            ElectricalProjectDistributionBoardFileId: electricalProjectDistributionBoardFileId,
                            ElectricalProjectFileId: file.ElectricalProjectFileId,
                            Kind: type,
                            IsDeleted: false,
                            LastUpdateDate: new Date().toISOString(),
                        },
                    ],
                });
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    function handleChange(fileList: FileList) {
        const file = fileList[0];

        if (!file)
            return;

        fileUpload.upload({
            file,
        });
    }

    return (
        <>
            <FileInput
                type="image"
                accept={fileUpload.accept ?? undefined}
                onChange={handleChange}
                disabled={isOnline && fileUpload.isDisabled}
                mode="both"
            />
            <div className="flex flex-col gap-2">
                {distributionBoard?.Files?.filter(file => file.Kind === type).map(file => (
                    <div
                        key={file.ElectricalProjectDistributionBoardFileId!}
                        className="relative flex flex-row gap-2"
                    >
                        <ElectricalProjectImage
                            className="h-[112px] w-full rounded-lg"
                            query={{ ElectricalProjectFileId: file.ElectricalProjectFileId! }}
                            expandable
                        />
                        <Button
                            className="absolute top-1 right-1 bg-background/50"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                                unlinkMutation.mutate({
                                    params: {
                                        query: {
                                            ElectricalProjectDistributionBoardFileId: file?.ElectricalProjectDistributionBoardFileId ?? '',
                                        },
                                    },
                                })}
                            disabled={fileDelete.isDisabled || !file?.ElectricalProjectDistributionBoardFileId || unlinkMutation.isPending}
                        >
                            {unlinkMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </Button>
                    </div>
                ))}
            </div>
        </>
    );
}

// /api/v2/electrical/projects/distributionboards/values/list
function GeneralInformationForm() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));
    const { data: valueData } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.options.distributionBoardValuesOptions());
    const { data: ingressRatings } = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.options.ingressProtectionRatingsOptions());

    const characticsOptions: { [key: string]: { label: string; value: string }[] } = {};

    valueData?.DeviceList?.forEach((item) => {
        const deviceName = item.Kind!;

        characticsOptions[deviceName] = item?.Devices?.map(characteristic => ({ label: characteristic.Value!, value: characteristic.Value! })) || [];
    });

    // "DiazedFuse" | "FuseLink" | "CircuitBreaker" | "Unknown"
    const deviceOptions = Object.keys(characticsOptions).map(device => ({ label: t(`distribution-box.information.form.devicelist.${device}`), value: device })) || [];
    const mainSwitchOptions = valueData?.MainSwitchNominalCurrents?.map(item => ({ label: item.Label!, value: item.Value! })) || [];

    mainSwitchOptions.push({ label: 'Anders', value: 0 });

    const ingressRatingOptions = ingressRatings?.Ratings?.map(item => ({ label: item.Description!, value: item.ElectricalIngressProtectionRatingId! })) || [];

    const { Field, handleSubmit, Subscribe, setFieldValue } = useForm<
        GeneralInformationFormValues,
        ZodValidator
    >({
        defaultValues: {
            earthingSystemKind: data?.DistributionBoard?.EarthingSystemKind || null,
            powerPhaseKind: data?.DistributionBoard?.PowerPhaseKind || null,
            electricalIngressProtectionRatingId: data?.DistributionBoard?.ElectricalIngressProtectionRatingId || '',
            applicanceProtectionClass: data?.DistributionBoard?.ApplicanceProtectionClass || null,
            mainSwitchNominalCurrent: data?.DistributionBoard?.IsMainSwitchNominalCurrentCustom ? 0 : data?.DistributionBoard?.MainSwitchNominalCurrent || null,
            mainSwitchNominalCurrentCustom: data?.DistributionBoard?.IsMainSwitchNominalCurrentCustom ? data?.DistributionBoard?.MainSwitchNominalCurrent || null : null,
            upstreamProtectiveDeviceKind: data?.DistributionBoard?.UpstreamProtectiveDeviceKind || '',
            upstreamProtectiveDeviceCharacteristic: data?.DistributionBoard?.IsUpstreamProtectiveDeviceCharacteristicCustom ? 'Anders' : data?.DistributionBoard?.UpstreamProtectiveDeviceCharacteristic || '',
            upstreamProtectiveDeviceCharacteristicCustom: data?.DistributionBoard?.IsUpstreamProtectiveDeviceCharacteristicCustom ? data?.DistributionBoard?.UpstreamProtectiveDeviceCharacteristic || '' : '',
            powerCircuitBreakerCurrent: data?.DistributionBoard?.PowerCircuitBreakerCurrent || null,
            powerCircuitBreakerType: data?.DistributionBoard?.PowerCircuitBreakerType || '',
            powerCircuitBreakerSetting: data?.DistributionBoard?.PowerCircuitBreakerSetting || '',
        },
        validatorAdapter: zodValidator(),
    });

    const patcher = usePatcher(
        '/api/v2/electrical/projects/distributionboards/patch',
        {
            onMutate: async (variables) => {
                const body = toPascalCase(variables!.body!);

                await queryClient.cancelQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId }));

                const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId });
                const previousDistributionBoardData = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['EditElectricalProjectDistributionBoardQueryResult'];

                const newDistributionBoardData: components['schemas']['EditElectricalProjectDistributionBoardQueryResult'] = {
                    ...previousDistributionBoardData,
                    DistributionBoard: {
                        ...previousDistributionBoardData.DistributionBoard,
                        ...body,
                    },
                };

                logger.debug({
                    message: 'onMutate(distributionBoardQueryOptions)',
                    path: '/api/v2/electrical/projects/distributionboards/patch',
                    distributionBoard: {
                        previousData: previousDistributionBoardData,
                        newData: newDistributionBoardData,
                        queryKey: distributionBoardQueryOptions.queryKey,
                        body,
                    },
                });

                queryClient.setQueryData(distributionBoardQueryOptions.queryKey, newDistributionBoardData);

                return {
                    previouseData: previousDistributionBoardData,
                    queryKey: distributionBoardQueryOptions.queryKey,
                };
            },
            onSettled: async () => await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardOptions({ electricalProjectDistributionBoardId: params.distributionboardId })),
            scope: {
                id: params.projectVersionId,
            },
        },
        {
            body: {
                ElectricalProjectDistributionBoardId: params.distributionboardId,
            },
        },
    );

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field
                name="earthingSystemKind"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.earthingSystemKind,
                    onChangeAsync: patcher.patchField('earthingSystemKind'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.stelsel.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                // { label: 'IT', value: 'IT' },
                                { label: 'TT', value: 'TT' },
                                { label: 'TN-C', value: 'TN-C' },
                                { label: 'TN-S', value: 'TN-S' },
                                { label: 'TN-CS', value: 'TN-CS' },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="powerPhaseKind"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.powerPhaseKind,
                    onChangeAsync: patcher.patchField('powerPhaseKind'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.fase.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: '1', value: '1' },
                                { label: '3', value: '3' },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="electricalIngressProtectionRatingId"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.electricalIngressProtectionRatingId,
                    onChangeAsync: patcher.patchField('electricalIngressProtectionRatingId'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.ipClassificatie.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={ingressRatingOptions}
                            render={SingleOptionItem}
                            dropdown
                            maxItemsBeforeScroll={10}
                            search
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="applicanceProtectionClass"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.applicanceProtectionClass,
                    onChangeAsync: patcher.patchField('applicanceProtectionClass'),
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.beschermingsklasse.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                // { label: '0', value: '0' },
                                // { label: '0I', value: '0I' },
                                { label: 'I', value: 'I' },
                                { label: 'II', value: 'II' },
                                // { label: 'III', value: 'III' },
                                // { label: 'IIFE', value: 'IIFE' },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="mainSwitchNominalCurrent"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.mainSwitchNominalCurrent,
                    onChangeAsync: async (value) => {
                        if (value.value === 0) {
                            return patcher.patchField('mainSwitchNominalCurrent')(value);
                        }

                        const result = await patcher.patchField('mainSwitchNominalCurrent')(value);

                        setFieldValue('mainSwitchNominalCurrentCustom', null);

                        return result;
                    },
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.hoofdschakelaar.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={mainSwitchOptions}
                            render={SingleOptionItem}
                            dropdown
                            maxItemsBeforeScroll={10}
                            search
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Subscribe selector={state => state.values.mainSwitchNominalCurrent}>
                {mainSwitchNominalCurrent =>
                    mainSwitchNominalCurrent === 0 && (
                        <>
                            <Field
                                name="mainSwitchNominalCurrentCustom"
                                validators={{
                                    onChange: validators.patchElectricalProjectDistributionBoardCommand.mainSwitchNominalCurrent,
                                    onChangeAsync: patcher.patchField('mainSwitchNominalCurrent'),
                                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('distribution-box.information.form.hoofdschakelaar.else.label')}>
                                        <NumberField
                                            name={name}
                                            onChange={handleChange}
                                            value={state.value}
                                            placeholder={t('distribution-box.information.form.hoofdschakelaar.else.placeholder')}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        </>
                    )}
            </Subscribe>
            <Field
                name="upstreamProtectiveDeviceKind"
                validators={{
                    onChange: validators.patchElectricalProjectDistributionBoardCommand.upstreamProtectiveDeviceKind,
                    onChangeAsync: async (value) => {
                        const response = await patcher.patchField('upstreamProtectiveDeviceKind')(value);
                        await patcher.patchField('upstreamProtectiveDeviceCharacteristic')({ value: null });

                        setFieldValue('upstreamProtectiveDeviceCharacteristic', '');
                        setFieldValue('upstreamProtectiveDeviceCharacteristicCustom', '');

                        return response;
                    },
                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('distribution-box.information.form.type.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                { label: 'Vermogensautomaat', value: 'PowerCircuitBreaker' },
                                ...deviceOptions,
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Subscribe selector={state => [state.values.upstreamProtectiveDeviceKind]}>
                {([upstreamProtectiveDeviceKind]) => (
                    <>
                        {(upstreamProtectiveDeviceKind !== '' && upstreamProtectiveDeviceKind !== 'PowerCircuitBreaker') && (
                            <Field
                                name="upstreamProtectiveDeviceCharacteristic"
                                validators={{
                                    onChange: validators.patchElectricalProjectDistributionBoardCommand.upstreamProtectiveDeviceCharacteristic,
                                    onChangeAsync: async (value) => {
                                        if (value.value === 'Anders') {
                                            return null;
                                        }

                                        const response = await patcher.patchField('upstreamProtectiveDeviceCharacteristic')(value);

                                        setFieldValue('upstreamProtectiveDeviceCharacteristicCustom', '');

                                        return response;
                                    },
                                    onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t(`distribution-box.information.form.devicelist.${upstreamProtectiveDeviceKind}`)}>
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            className="flex flex-row items-center justify-between"
                                            options={characticsOptions[upstreamProtectiveDeviceKind]}
                                            render={SingleOptionItem}
                                            dropdown
                                            search
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        )}
                        {(upstreamProtectiveDeviceKind === 'PowerCircuitBreaker') && (
                            <div className="flex flex-col gap-2 ps-4 bg-background-secondary p-2 rounded">
                                <Field
                                    name="powerCircuitBreakerType"
                                    validators={{
                                        onChange: validators.patchElectricalProjectDistributionBoardCommand.powerCircuitBreakerType,
                                        onChangeAsync: patcher.patchField('powerCircuitBreakerType'),
                                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('distribution-box.information.form.powerCircuitBreaker.type.label')}>
                                            <Input
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('distribution-box.information.form.powerCircuitBreaker.type.placeholder')}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                                <Field
                                    name="powerCircuitBreakerSetting"
                                    validators={{
                                        onChange: validators.patchElectricalProjectDistributionBoardCommand.powerCircuitBreakerSetting,
                                        onChangeAsync: patcher.patchField('powerCircuitBreakerSetting'),
                                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('distribution-box.information.form.powerCircuitBreaker.setting.label')}>
                                            <Input
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('distribution-box.information.form.powerCircuitBreaker.setting.placeholder')}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                                <Field
                                    name="powerCircuitBreakerCurrent"
                                    validators={{
                                        onChange: validators.patchElectricalProjectDistributionBoardCommand.powerCircuitBreakerCurrent,
                                        onChangeAsync: patcher.patchField('powerCircuitBreakerCurrent'),
                                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('distribution-box.information.form.powerCircuitBreaker.amperage.label')}>
                                            <NumberField
                                                name={name}
                                                onChange={handleChange}
                                                value={state.value}
                                                placeholder={t('distribution-box.information.form.powerCircuitBreaker.amperage.placeholder')}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                            </div>
                        )}
                    </>
                )}
            </Subscribe>
            <Subscribe selector={state => state.values.upstreamProtectiveDeviceCharacteristic}>
                {upstreamProtectiveDeviceCharacteristic =>
                    upstreamProtectiveDeviceCharacteristic === 'Anders' && (
                        <Field
                            name="upstreamProtectiveDeviceCharacteristicCustom"
                            validators={{
                                onChange: validators.patchElectricalProjectDistributionBoardCommand.upstreamProtectiveDeviceCharacteristic,
                                onChangeAsync: patcher.patchField('upstreamProtectiveDeviceCharacteristic'),
                                onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label label={t('distribution-box.information.form.type.else.label')}>
                                    <Input
                                        name={name}
                                        onChange={e => handleChange(e.target.value)}
                                        value={state.value}
                                        placeholder={t('distribution-box.information.form.type.else.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    )}
            </Subscribe>
            <Observation.Table />
            <Observation.CreateButton className="mx-auto" />
        </form>
    );
}
